import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Single source of truth for the canonical site URL.
// Change this only when the domain changes.
export const SITE_URL = "https://baishalii.in";

const DEFAULT_TITLE = "BAISHALII — Tanks, Pipes & Fittings | A name that justifies TRUST";
const DEFAULT_DESC = "Baishalii Tanks & Pipes — India's trusted manufacturer of water tanks, uPVC/CPVC pipes & fittings, SS sinks, cisterns, PTMT taps and garden pipes.";

const NOINDEX_PREFIXES = ["/admin", "/vendor", "/customer"];

/**
 * True when the given pathname should NOT be indexed (portal / auth routes).
 */
function isNoindex(pathname) {
  return NOINDEX_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Build the canonical URL for a given pathname.
 * Strips query strings, hash fragments and trailing slashes (except root).
 */
export function canonicalFor(pathname) {
  let p = (pathname || "/").split("?")[0].split("#")[0];
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return `${SITE_URL}${p}`;
}

/** Ensure a <link rel="canonical"> exists and points to the given href. */
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

/** Ensure a <meta name|property="..."> exists and holds the given content. */
function setMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

/** Remove nodes we previously injected but no longer want (e.g. on noindex routes). */
function removeSelector(selector) {
  const el = document.querySelector(selector);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/**
 * Apply title, description, canonical, og:url, og:title, og:description and robots
 * meta tags for the current route. Non-indexable routes (admin/vendor/customer)
 * get `robots: noindex,nofollow` and their canonical is removed to avoid signalling
 * them as canonical from the frontend.
 */
export function usePageMeta(title, description) {
  const loc = useLocation();
  useEffect(() => {
    const finalTitle = title ? `${title} | BAISHALII` : DEFAULT_TITLE;
    const finalDesc = description || DEFAULT_DESC;
    document.title = finalTitle;
    setMeta("name", "description", finalDesc);

    const noindex = isNoindex(loc.pathname);
    if (noindex) {
      setMeta("name", "robots", "noindex,nofollow");
      removeSelector('link[rel="canonical"]');
      removeSelector('meta[property="og:url"]');
      return;
    }

    setMeta("name", "robots", "index,follow");
    const url = canonicalFor(loc.pathname);
    setLink("canonical", url);
    setMeta("property", "og:url", url);
    setMeta("property", "og:title", finalTitle);
    setMeta("property", "og:description", finalDesc);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "BAISHALII");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", finalTitle);
    setMeta("name", "twitter:description", finalDesc);
  }, [title, description, loc.pathname]);
}

/**
 * Global SEO manager — mount once inside <BrowserRouter>. It listens to route
 * changes and ensures every route has correct canonical + robots tags, even
 * routes that don't call usePageMeta (e.g. portal/auth screens).
 */
export function SEOManager() {
  const loc = useLocation();
  useEffect(() => {
    if (isNoindex(loc.pathname)) {
      setMeta("name", "robots", "noindex,nofollow");
      removeSelector('link[rel="canonical"]');
      removeSelector('meta[property="og:url"]');
      return;
    }
    setMeta("name", "robots", "index,follow");
    const url = canonicalFor(loc.pathname);
    setLink("canonical", url);
    setMeta("property", "og:url", url);
  }, [loc.pathname]);
  return null;
}

